import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import HubIcon from '@mui/icons-material/Hub';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import BadgeIcon from '@mui/icons-material/Badge';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Progress } from '@backstage/core-components';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { ProfileLinkingSearch } from './ProfileLinkingSearch';
import { SourceProfile } from './SourceProfile';
import { CandidateCard } from './CandidateCard';
import { EvidenceDrawer } from './EvidenceDrawer';
import { DecisionBadge } from './DecisionBadge';
import { UpsAuthPanel } from './UpsAuthPanel';
import { useUpsAuthContext } from './UpsAuthContext';
import { useProfileLinkingExplorer } from './useProfileLinkingExplorer';
import type { SearchParams } from './api';
import type { Decision, UiCandidate } from './types';

const SUMMARY_DECISIONS: { decision: Decision; label: string }[] = [
  { decision: 'STRONG_MATCH', label: 'Strong match' },
  { decision: 'INCONCLUSIVE', label: 'Inconclusive' },
  { decision: 'STRONG_MISMATCH', label: 'Strong mismatch' },
];

const INTRO_CAPABILITIES = [
  { icon: HubIcon, label: 'Find connected identities' },
  { icon: FactCheckIcon, label: 'Understand why they match' },
  { icon: ImageSearchIcon, label: 'Inspect image evidence' },
];

const PRE_SEARCH_CAPABILITIES = [
  { icon: BadgeIcon, label: 'Profile metadata' },
  { icon: CompareArrowsIcon, label: 'Handle similarity' },
  { icon: ImageSearchIcon, label: 'Image evidence' },
  { icon: BarChartIcon, label: 'Score contribution breakdown' },
];

const CapabilityList = ({
  items,
  compact = false,
}: {
  items: { icon: typeof HubIcon; label: string }[];
  compact?: boolean;
}) => {
  const theme = useTheme();
  return (
    <Stack direction="row" flexWrap="wrap" spacing={compact ? 1.5 : 2.5} useFlexGap>
      {items.map(({ icon: Icon, label }) => (
        <Stack alignItems="center" direction="row" key={label} spacing={0.6}>
          <Icon sx={{ color: theme.palette.primary.main, fontSize: compact ? 15 : 17 }} />
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: compact ? 11.5 : 12.5, fontWeight: 500 }}>
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const EmptyState = ({
  icon,
  title,
  message,
  onRetry,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  onRetry?: () => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        p: 5,
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: theme.palette.text.secondary, fontSize: 36, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13, maxWidth: 420 }}>
        {message}
      </Typography>
      {onRetry && (
        <Button onClick={onRetry} size="small" variant="outlined">
          Retry
        </Button>
      )}
    </Stack>
  );
};

const PreSearchEmptyState = () => {
  const theme = useTheme();
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={{
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px dashed ${getSurfaceBorder(theme, 'strong')}`,
        borderRadius: `${radii.xl}px`,
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Start with a known profile</Typography>
      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13, maxWidth: 440 }}>
        Search for a social profile to discover connected identities across networks and inspect
        how each candidate was scored.
      </Typography>
      <CapabilityList items={PRE_SEARCH_CAPABILITIES} compact />
    </Stack>
  );
};

/**
 * The single shared Profile Linking experience: intro, auth panel, search
 * controls, source profile, candidate cards, and evidence drawer. Rendered
 * embedded under the Unified Profiles API definition page (see
 * PrettyEntityApiDefinitionCard) — `embedded` affects ONLY heading/spacing
 * chrome; the search/lookup/evidence/scoring logic below is exactly one
 * implementation. Must be rendered inside a <UpsAuthProvider>.
 */
export const ProfileLinkingExplorer = ({ embedded = false }: { embedded?: boolean }) => {
  const theme = useTheme();
  const upsAuth = useUpsAuthContext();
  const { state, search, retry } = useProfileLinkingExplorer();
  const [selectedCandidate, setSelectedCandidate] = useState<UiCandidate | null>(null);

  // UPS rejected/expired the token — drop it so the user re-authorizes
  // rather than silently retrying with a known-bad token.
  useEffect(() => {
    if (state.status === 'ups-auth-failed') upsAuth.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  const handleSearch = (params: SearchParams) => {
    if (!upsAuth.token) return;
    void search(params, upsAuth.token);
  };

  const candidates = state.status === 'success' ? state.data.candidates : [];
  const counts = SUMMARY_DECISIONS.map(({ decision, label }) => ({
    decision,
    label,
    count: candidates.filter(c => c.decision === decision).length,
  }));
  const otherCount = candidates.length - counts.reduce((sum, c) => sum + c.count, 0);

  const content = (
    <Stack spacing={embedded ? 2 : 3}>
      {/* Intro — standalone relies on the page's own title (PageWithHeader),
          so it shows description + indicators only; embedded has no other
          heading, so it shows its own compact title too. */}
      <Box>
        {embedded && (
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>
            Cross-Network Profile Intelligence
          </Typography>
        )}
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: embedded ? 12.5 : 13.5, mb: 1 }}>
          Discover and inspect identities across social networks using Unified Profiles linking
          evidence.
        </Typography>
        <CapabilityList items={INTRO_CAPABILITIES} compact={embedded} />
      </Box>

      <UpsAuthPanel embedded={embedded} />

      <Box>
        <Typography sx={{ fontSize: embedded ? 13.5 : 15, fontWeight: 700, mb: 0.25 }}>
          Search for a known profile
        </Typography>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: embedded ? 12 : 12.5, mb: 1.25 }}>
          Start with an exact social handle or native profile ID. We'll find known cross-network
          candidates and explain the evidence behind each match.
        </Typography>
        <ProfileLinkingSearch
          authorized={upsAuth.isAuthorized}
          loading={state.status === 'loading'}
          onSearch={handleSearch}
        />
      </Box>

      {state.status === 'loading' && <Progress />}

      {state.status === 'initial' && upsAuth.isAuthorized && <PreSearchEmptyState />}

      {state.status === 'not-found' && (
        <EmptyState icon={<SearchOffIcon fontSize="inherit" />} title="Profile not found" message={state.message} />
      )}

      {state.status === 'ups-auth-failed' && (
        <EmptyState
          icon={<LockOutlinedIcon fontSize="inherit" />}
          title="Authorization failed"
          message="Your Unified Profiles bearer token was rejected or may have expired."
        />
      )}

      {state.status === 'ups-network-error' && (
        <EmptyState
          icon={<WifiOffIcon fontSize="inherit" />}
          title="Unable to reach Unified Profiles"
          message="Unable to reach Unified Profiles from the browser."
        />
      )}

      {state.status === 'storage-error' && (
        <EmptyState
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          title="Service unavailable"
          message={state.message}
          onRetry={retry}
        />
      )}

      {state.status === 'error' && (
        <EmptyState
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          title="Service unavailable"
          message={state.message}
          onRetry={retry}
        />
      )}

      {state.status === 'success' && (
        <>
          <SourceProfile profile={state.data.sourceProfile} />

          <Box>
            <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                Identities found ({candidates.length})
              </Typography>
              <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
                {counts.map(c => (
                  <Stack alignItems="center" direction="row" key={c.decision} spacing={0.5}>
                    <DecisionBadge decision={c.decision} />
                    <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary }}>
                      {c.count}
                    </Typography>
                  </Stack>
                ))}
                {otherCount > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary }}>
                    {otherCount} other
                  </Typography>
                )}
              </Stack>
            </Stack>

            {candidates.length === 0 ? (
              <EmptyState
                icon={<SearchOffIcon fontSize="inherit" />}
                title="No candidates found"
                message="This profile was resolved, but no linked identities on other networks were found."
              />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                }}
              >
                {candidates.map(candidate => (
                  <CandidateCard
                    candidate={candidate}
                    key={candidate.id}
                    onViewEvidence={() => setSelectedCandidate(candidate)}
                  />
                ))}
              </Box>
            )}
          </Box>

          <EvidenceDrawer
            candidate={selectedCandidate}
            onClose={() => setSelectedCandidate(null)}
            open={selectedCandidate !== null}
            sourceProfile={state.data.sourceProfile}
          />
        </>
      )}
    </Stack>
  );

  if (!embedded) return content;

  return <Box sx={{ mt: 4 }}>{content}</Box>;
};
